import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDate, NgbDatepickerNavigateEvent, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { Util } from '@libs/utilities/util';

import { EnumNgDatePickerTextAlignMent, INgDatePickerOptions } from '../shared/date-picker.interface';
import { NgAbstractInputComponent } from '../../shared/inputs.abstract';
import { INgCustomDropdownOption } from '../../dropdowns/custom-dropdown/custom-dropdown.interface';
import { createMonthOptions, createYearOptions } from '../shared/date-navigation.util';


@Component({
    selector: 'ng-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgDatePickerComponent,
            multi: true
        }
    ],
    standalone: false
})
export class NgDatePickerComponent extends NgAbstractInputComponent<Date> implements ControlValueAccessor {

	//** Configurations */
	private readonly DATE_PICKER_DEFAULT_OPTIONS: INgDatePickerOptions = {
		minDate			: new Date('1970-01-01'),
		maxDate			: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
		disableDates	: [new Date()]
	};

	@Input() options 	: INgDatePickerOptions = this.DATE_PICKER_DEFAULT_OPTIONS;
	@Input() textAlign 	: EnumNgDatePickerTextAlignMent = EnumNgDatePickerTextAlignMent.Center;

	//** Enums */
	EnumNgDatePickerTextAlignMent: typeof EnumNgDatePickerTextAlignMent = EnumNgDatePickerTextAlignMent;

	//** Calendar navigation */
	readonly calendarMonthOptions: INgCustomDropdownOption<number>[] = createMonthOptions();
	calendarYearOptions: INgCustomDropdownOption<number>[] = createYearOptions(this.options);
	calendarMonth: number = new Date().getMonth() + 1;
	calendarYear: number = new Date().getFullYear();

	constructor(
		private cdr: ChangeDetectorRef,
		@Inject(DOCUMENT) private readonly document: Document
	) {
		super('date-picker');
	}

	get focusRestoreTarget(): HTMLElement {
		return this.document.body;
	}


	/**------------------------------------------------------
	 * Control Value Accessor Functions
	 */
	override writeValue(value: Date): void {

		//0 - check if initial date is to be disabled
		if (this.isDateDisabled(value)) {
			this.value = null!;
			return;
		}

		//1 - else update value
		this.value = value;
	}


	/**------------------------------------------------------
	 * Ui Helper Functions
	 */
	emitDate(selectedDate: NgbDate): void {
		if (!selectedDate) return;
		this.value = Util.Date.fromNgbDate(selectedDate);
		this.onChange(this.value);
		this.onTouched();
	}

	toggleCalendar(datepicker: NgbInputDatepicker): void {
		if (this.isDisabled) return;
		datepicker.toggle();
	}

	syncCalendarNavigation(event: NgbDatepickerNavigateEvent): void {
		this.calendarMonth = event.next.month;
		this.calendarYear = event.next.year;
		this.calendarYearOptions = createYearOptions(this.options);
		this.cdr.markForCheck();
	}

	navigateCalendar(datepicker: NgbInputDatepicker, month: number = this.calendarMonth, year: number = this.calendarYear): void {
		datepicker.navigateTo({ year, month, day: 1 });
	}

	//** Disable dates provided in options */
	disableDates = (ngbDate: NgbDate): boolean => {
		const date: Date = Util.Date.fromNgbDate(ngbDate);
		return this.isDateDisabled(date);
	};


	/**------------------------------------------------------
	 * Helper Functions
	 */

	//** check if provided date is present in disabled dates */
	private isDateDisabled(date: Date): boolean {

		//0 - return if date undefined
		if (!date) return false;

		//1 - does the date exists (is disabled)
		if (Util.Array.isEmpty(this.options.disableDates)) return false;
		const existingDate: Date | null = this.options.disableDates.find((disableDate: Date) => Util.Date.compareByDateString(date, disableDate))!;
		if (!existingDate) return false;

		//2 - date is not disabled
		return true;
	}
}
